import { FetchSource, type RangeResponse, type Source } from 'pmtiles';

/**
 * Zwischenspeicher für Kartenausschnitte.
 *
 * Auf Nebenstrecken ist der Empfang streckenweise weg — und ausgerechnet dort
 * wird die Karte gebraucht. Deshalb bleibt jeder einmal geladene Ausschnitt
 * erhalten und ist danach auch ohne Verbindung da.
 *
 * ## Warum das nicht der Service Worker erledigt
 *
 * Die Kartendatei ist eine einzige Datei von mehreren hundert Megabyte. Der
 * Browser holt daraus nur die Bereiche, die er gerade braucht — ein paar
 * Kilobyte je Kachel. Ein Service Worker kann nur ganze Antworten ablegen; um
 * einen Bereich später beantworten zu können, müsste er die vollständige Datei
 * vorhalten. Deshalb liegt der Zwischenspeicher hier, eine Ebene tiefer: Jeder
 * angeforderte Bereich wird unter einem eigenen Schlüssel abgelegt und beim
 * nächsten Mal von dort beantwortet.
 */

const CACHE_NAME = 'konvoi-karte-v1';

/** Eigener Kopfeintrag, unter dem die Kennung der Kartendatei mitgeführt wird. */
const ETAG_HEADER = 'x-konvoi-etag';

/** Ein Bereich der Kartendatei bekommt einen eigenen, künstlichen Schlüssel. */
function keyFor(url: string, offset: number, length: number): string {
  return `${url}?bereich=${offset}-${offset + length - 1}`;
}

export class CachingPMTilesSource implements Source {
  readonly #inner: FetchSource;
  readonly #url: string;

  constructor(url: string) {
    this.#url = url;
    this.#inner = new FetchSource(url);
  }

  getKey(): string {
    return this.#url;
  }

  async getBytes(offset: number, length: number, signal?: AbortSignal): Promise<RangeResponse> {
    const schlüssel = keyFor(this.#url, offset, length);
    const speicher = await caches.open(CACHE_NAME).catch(() => null);

    if (speicher !== null) {
      const abgelegt = await speicher.match(schlüssel).catch(() => undefined);
      if (abgelegt !== undefined) {
        // Die Kennung der Datei muss mit abgelegt und zurückgegeben werden.
        // Ohne sie kann PMTiles nicht erkennen, ob die Kartendatei inzwischen
        // eine andere ist — und bricht ab, statt weiterzulesen. Ein
        // Zwischenspeicher, der die Kennung wegwirft, hält die Karte an.
        const kennung = abgelegt.headers.get(ETAG_HEADER);
        return {
          data: await abgelegt.arrayBuffer(),
          ...(kennung === null || kennung === '' ? {} : { etag: kennung }),
        };
      }
    }

    const antwort = await this.#inner.getBytes(offset, length, signal);

    if (speicher !== null) {
      // Das Ablegen darf den Kartenaufbau nicht aufhalten und auch nicht
      // scheitern lassen — ist der Speicher voll, wird eben nichts abgelegt.
      void speicher
        .put(
          schlüssel,
          new Response(antwort.data.slice(0), {
            headers: antwort.etag === undefined ? {} : { [ETAG_HEADER]: antwort.etag },
          }),
        )
        .catch(() => undefined);
    }

    return antwort;
  }
}

/**
 * Wirft die Bereiche weg, die zu einer anderen Kartendatei gehören.
 *
 * Der Schlüssel eines abgelegten Bereichs enthält die Adresse der Kartendatei.
 * Wird das Gebiet neu zugeschnitten und die Datei heißt anders, sind die alten
 * Einträge nicht mehr erreichbar — und lägen trotzdem für immer auf dem Gerät.
 * Auf einem Telefon sind das die Ausschnitte einer ganzen Ausfahrt.
 *
 * Bewusst nicht der ganze Speicher: Was zur aktuellen Datei gehört, ist genau
 * das, was ohne Verbindung noch da sein soll. Es beim Start wegzuwerfen hieße,
 * die Zusage im ersten Funkloch zu brechen.
 */
export async function pruneTileCache(url: string): Promise<void> {
  const speicher = await caches.open(CACHE_NAME).catch(() => null);
  if (speicher === null) {
    return;
  }

  const schlüssel = await speicher.keys().catch(() => []);
  await Promise.all(
    schlüssel
      .filter((anfrage) => !anfrage.url.startsWith(url))
      .map((anfrage) => speicher.delete(anfrage).catch(() => undefined)),
  );
}
